import { ETextSize } from '@/components/atoms/at-text/at-text.types';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
  TClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import {
  isIImageGlobalCSProps,
  isIImageProps,
} from '@/components/atoms/at-media/utils';

import {
  EThemes,
  IAtIconCsProps,
  IAtLinkGlobalFieldProps,
  IDefaultCsProps,
  IImageGlobalCSProps,
  IMlCtaCardBlockCS,
  IPromo,
} from '../types';
import { IAtClickableProps } from '@/components/atoms/at-clickable/at-clickable';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { THeaderSupportedElements } from '@/components/atoms/at-header/at-header.types';
import {
  EMlCardsTypes,
  IAtChipProps,
} from '@/components/molecules/ml-card/ml-card.types';
import {
  EAlignment,
  EMlHeaderSize,
  EOrientation,
  IMlHeaderCMSProps,
  IMlHeaderProps,
} from '@/components/molecules/ml-header/ml-header.types';
import { IMlCtaCardProps } from '@/components/molecules/ml-cta-card/ml-cta-card.types';

export interface IAtIconCMSProps extends IDefaultCsProps {
  name: { icon: string };
  size: ETextSize;
}

export interface IAtChipCMSProps {
  label: string;
  icon: IAtIconCMSProps[];
  title: string;
  variant: ETagVariants;
}

export interface IAtLinkCMSProps extends IDefaultCsProps {
  variant?: { variant: TClickableVariants };
  icon: IAtIconCMSProps[];
  outline: boolean;
  size: EClickableSize;
  type: EClickableTypes;
  underline: boolean;
  target: EClickableTarget;
  title: string;
  label: string;
  url: string;
}

export interface IImageCMSProps {
  alt: string;
  desktop_image_url: string;
  entry_title: string;
  mobile_image_url: string;
  tablet_image_url: string;
}

export interface IParsedPromo {
  chipProps: {
    label: string;
    icon: EIcons;
  };
  ctaProps: {
    label: string;
    href: string;
    variant: EClickableVariants;
    type: EClickableTypes;
    outline: boolean;
  };
  image: IAtMediaProps | undefined;
  title: string;
  body: string;
  variant: EMlCardsTypes;
}

interface IParsePromoProps {
  isPromoBlock?: boolean;
  promos: IPromo[];
}

/**
 * Extracts the icon name from the CMS-provided icon array.
 *
 * Only the first icon in the array is used. If the array is empty, returns undefined.
 *
 * @param icon - Array of CMS icon objects (may be legacy or current shape)
 * @returns The extracted EIcons enum value, or undefined if not found
 */
export const parseIconFromCMS = (
  icon: (IAtIconCMSProps | IAtIconCsProps)[] | undefined,
): EIcons | undefined => {
  if (!icon?.length) return;

  const iconName = icon[0].name?.icon as EIcons;
  return iconName;
};

/**
 * Parses the first chip in a CMS chip group and maps it to a valid `IAtChipProps` object.
 *
 * Ignores additional chips beyond the first.
 *
 * @param chips - Optional array of CMS chip entries
 * @returns Parsed chip props for `MlCard` components, or undefined if no chip is provided
 */
export const parseChipFromCMS = (
  chips: IAtChipCMSProps[] | undefined,
): IAtChipProps | undefined => {
  if (!chips?.length) return;

  const chip = chips[0];

  return {
    icon: parseIconFromCMS(chip.icon),
    label: chip.label,
    variant: chip.variant,
  };
};

/**
 * Parses a global CMS link field and maps it to `IAtClickableProps`.
 *
 * Intended for use with single-entry global link fields (e.g., Contentstack modular blocks).
 * Automatically maps icon, target, variant, and accessibility values.
 *
 * @param cta - The CMS global link field object
 * @param interactLocation - Optional GTM tracking location string
 * @returns A fully structured `IAtClickableProps` object, or undefined if no CTA is provided
 */
export const parseGlobalLinkFromCMS = (
  cta: IAtLinkGlobalFieldProps | undefined,
  interactLocation: string | undefined,
): IAtClickableProps | undefined => {
  if (!cta) return;

  const {
    icon,
    outline,
    size,
    target,
    type,
    aria_label,
    url: { title: link_label, href },
    variant,
  } = cta;

  return {
    href: href,
    target: EClickableTarget[target as keyof typeof EClickableTarget],
    type,
    variant: EClickableVariants[variant],
    size: EClickableSize[size],
    icon: parseIconFromCMS(icon as IAtIconCsProps[]),
    outline,
    aria_label,
    label: link_label,
    interactLocation: interactLocation,
  } as IAtClickableProps;
};

/**
 * Parses the first item in a group of CMS link entries into `IAtClickableProps`.
 *
 * Used for non-global links passed as reference arrays. Prioritizes the first entry only.
 * Handles fallback for tertiary underline style if variant is not explicitly set.
 *
 * @param ctas - An array of CMS link objects
 * @param interactLocation - Optional GTM tracking location string
 * @returns A structured clickable object for rendering a button or link, or undefined if empty
 */
export const parseLinkFromCMS = (
  ctas: IAtLinkCMSProps[] | undefined,
  interactLocation: string | undefined,
): IAtClickableProps | undefined => {
  if (!ctas?.length) return;

  const {
    variant,
    icon,
    outline,
    type,
    underline,
    label,
    size,
    target,
    title,
    url,
  } = ctas[0];

  return {
    href: url,
    target,
    type,
    variant: underline ? EClickableVariants.TERTIARY : variant?.variant,
    outline,
    size,
    label: label || title,
    children: icon,
    interactLocation,
  } as IAtClickableProps;
};

/**
 * Normalizes image data from CMS into a shape consumable by `AtMedia`.
 *
 * Supports both global image references and direct media fields. Returns undefined
 * if no image is provided.
 *
 * @param cmsImage - A CMS image entry, either global or inline, or undefined/null
 * @returns A fully structured `IAtMediaProps` object with responsive image sources and alt text, OR undefined
 */
export const parseImageFromCMS = (
  cmsImage: IImageCMSProps | IImageGlobalCSProps | undefined | null,
): IAtMediaProps | undefined => {
  if (!cmsImage) {
    return undefined;
  }

  if (isIImageGlobalCSProps(cmsImage)) {
    const {
      mobile_image_url: mobile,
      mobile_position,
      mobile_aspect_ratio,
      tablet_image_url: tablet,
      tablet_position,
      tablet_aspect_ratio,
      desktop_image_url: desktop,
      desktop_aspect_ratio,
      desktop_position,
      priority,
      quality,
      decorative,
    } = cmsImage;

    return {
      decorative,
      priority,
      position: {
        mobile: mobile_position,
        tablet: tablet_position || '',
        desktop: desktop_position || '',
      },
      quality: Number(quality),
      aspectRatio: {
        mobile: mobile_aspect_ratio || 'auto',
        tablet: tablet_aspect_ratio || '',
        desktop: desktop_aspect_ratio || '',
      },
      alt: {
        mobile: mobile?.description || '',
        tablet: tablet?.description || '',
        desktop: desktop?.description || '',
      },
      src: {
        // while this is required, there are instances with live preview
        // where this component has been added to the page, but the image has not been selected yet
        mobile: mobile?.url || '/assets/FPO.webp',
        tablet: tablet?.url || '',
        desktop: desktop?.url || '',
      },
    };
  }

  if (isIImageProps(cmsImage)) {
    const {
      alt,
      desktop_image_url: desktop,
      mobile_image_url: mobile,
      tablet_image_url: tablet,
    } = cmsImage;

    return {
      alt,
      src: {
        mobile,
        tablet,
        desktop,
      },
    };
  }

  return undefined;
};

/**
 * Parses a CMS header block into `IMlHeaderProps` for use in `MlHeader` components.
 *
 * Includes alignment, order, size, and optional CTA handling.
 * Gracefully falls back if optional fields (like alignment or size) are missing.
 *
 * @param headerCMS - The CMS header entry object
 * @returns Parsed header props with mapped enums and CTA
 */
export const parseHeaderFromCMS = (
  headerCMS: IMlHeaderCMSProps,
): IMlHeaderProps => {
  return {
    head: headerCMS.head,
    headElement: headerCMS?.head_element?.select,
    body: headerCMS.body,
    ctaProps: parseLinkFromCMS(headerCMS.cta, 'ml-header'),
    alignment: headerCMS?.alignment?.length
      ? (headerCMS.alignment.toLowerCase() as EAlignment)
      : undefined,
    order: headerCMS.text_order?.length
      ? (headerCMS.text_order.toLowerCase() as EOrientation)
      : undefined,
    size: headerCMS.size?.length
      ? (headerCMS.size.toLowerCase() as EMlHeaderSize)
      : undefined,
  };
};

/**
 * Transforms a list of CMS promo entries into an array of `IParsedPromo` objects.
 *
 * Used by promo components (e.g. `MlPromoCard`, `MlPromoCompactCard`) to extract
 * chip, CTA, image, and layout metadata.
 *
 * Automatically applies `EMlCardsTypes.PROMO_COMPACT` if the promo is rendered inside a block.
 *
 * @param {Object} params
 * @param {IPromo[]} params.promos - Array of CMS promo entries
 * @param {boolean} [params.isPromoBlock=false] - Optional flag indicating whether promos are in a block
 * @returns {IParsedPromo[]} Array of parsed promo objects ready for rendering
 */
export const parsePromos = ({
  promos,
  isPromoBlock = false,
}: IParsePromoProps): IParsedPromo[] => {
  if (promos.length <= 0) {
    return [];
  }
  return promos?.map((promo: IPromo) => {
    const {
      chip_copy,
      cta,
      promo_description: description,
      promo_image: img,
      image,
      title,
    } = promo;

    return {
      chipProps: {
        label: chip_copy,
        icon: EIcons.PRICE,
      },
      ctaProps: {
        label: cta.label,
        href: cta.url.href,
        variant: EClickableVariants[cta.variant],
        type: cta.type as EClickableTypes,
        outline: cta.outline,
      },
      image: parseImageFromCMS(image),
      title,
      body: description,
      variant: isPromoBlock ? EMlCardsTypes.PROMO_COMPACT : EMlCardsTypes.PROMO,
    };
  });
};

/**
 * Converts a CMS CTA Card block into a valid `IMlCtaCardProps` object.
 *
 * Includes handling for icon, background image, theme, heading element, variant, and optional overlay.
 * Designed for use with `MlCtaCard` molecule or promo-style hero cards.
 *
 * @param card - A single CMS CTA Card block entry
 * @returns The fully parsed `IMlCtaCardProps` config object
 */
export const parseCTACardFromCMS = ({
  content_title,
  aria_label,
  icon,
  link,
  background_image,
  theme,
  description,
  show_cta,
  headingAlt,
  heading_element,
  heading_variant,
  size,
  use_overlay,
}: IMlCtaCardBlockCS): IMlCtaCardProps => {
  return {
    title: content_title,
    icon: icon ? icon.icon : undefined,
    link,
    bgImage: background_image,
    theme: theme?.theme ? (theme.theme.toLowerCase() as EThemes) : EThemes.DARK,
    description,
    showCta: show_cta,
    ariaLabel: aria_label,
    headingAlt,
    showOverlay: use_overlay || false,
    headingElement:
      (heading_element?.select as THeaderSupportedElements) || 'h2',
    headingVariant:
      (heading_variant?.select as THeaderSupportedElements) || 'h3',
    size,
  };
};
